import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import { useDispatch } from "react-redux";
import { TOGGLE_LAYOUT } from "../../../store/layout/Actions";
import {
  AfterSalesClientCard,
  AfterSalesSummaryCards,
  AssignOwnerDialog,
  FiltersSection,
} from "./UI";
import { FilterAfterSalesClient } from "../../../Services/AfterSalesServices/AfterSalesServices";
import { useVerticalNav } from "../../../Contexts/VerticalNavContext";
import {
  AfterSalesClientCardSkeleton,
  AfterSalesSummaryCardsListSkeleton,
  CustomBadge,
  CustomBreadCrumb,
  CustomButton,
  CustomPagination,
  CustomPermission,
  NoDataFound,
  PageTitle,
} from "../../../Components";
import CreateLeadDialog from "../../../Components/CreateLeadDialog/CreateLeadDialog";
import { LinkOfferDialog } from "../AfterSalesClientProfileView/UI";
import { AddNewActivityModalDialog } from "../../../Components/V2/Header/UI/AddQuickActivityRelatedDialogs/AddNewActivityModalDialog";
import {
  useLayoutDetection,
  useSelectedTheme,
  useTranslate,
} from "../../../Hooks";

// styles
import useStyles from "./styles";

// icons
import { UserCheck01 } from "../../../assets/icons";
import { AfterSalePermissions } from "../../../Permissions";

export function AfterSalesView() {
  const styles = useStyles();
  const dropPagination = useRef(null);
  const stickyPagination = useRef(null);
  const { isNewLayout: isReskinningLayout, hasNewLayout } =
    useLayoutDetection();
  const isNewLayout = isReskinningLayout && hasNewLayout;
  const dispatch = useDispatch();
  const { translate } = useTranslate("AfterSalesView");
  const { setAlertBoxContent, setStickyBottomBox } = useVerticalNav();
  const {
    theme: { palette },
  } = useSelectedTheme();

  const [filters, setFilters] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 1, pageSize: 25 });
  const [inputValue, setInputValue] = useState(pagination.pageIndex);
  const [clientData, setClientData] = useState({
    result: [],
    totalCount: 0,
  });

  const [selectedClient, setSelectedClient] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState({
    ActivitiesDialog: false,
    LinkOfferDialog: false,
    CreateNewLeadDialog: false,
    AssignOwnerDialog: false,
  });
  const [checkedCards, setCheckedCards] = useState([]);

  const handleOnCardCheck = (id) => {
    if (checkedCards.includes(id)) {
      setCheckedCards(checkedCards.filter((cardId) => cardId !== id));
    } else {
      setCheckedCards([...checkedCards, id]);
    }
  };
  const handleAssignOwnerCloseClick = () => {
    setIsDialogOpen((prev) => ({ ...prev, AssignOwnerDialog: false }));
    setCheckedCards([]);
  };

  const handleClientClick = (client) => {
    setSelectedClient(client);
    setIsDialogOpen((prev) => ({ ...prev, LinkOfferDialog: true }));
  };
  const handleDialogClose = () => {
    setSelectedClient(null);
    setIsDialogOpen((prev) => ({ ...prev, LinkOfferDialog: false }));
  };

  const handleCreateNewLeadClick = (client) => {
    setSelectedClient(client);
    setIsDialogOpen((prev) => ({ ...prev, CreateNewLeadDialog: true }));
  };
  const handleCreateNewLeadCloseClick = (value) => {
    setSelectedClient(null);
    setIsDialogOpen((prev) => ({ ...prev, CreateNewLeadDialog: value }));
  };
  const handleCreateNewActivityClick = (client) => {
    setSelectedClient(client);
    setIsDialogOpen((prev) => ({ ...prev, ActivitiesDialog: true }));
  };

  const cardActions = {
    linkOffer: handleClientClick,
    createNewLead: handleCreateNewLeadClick,
    createNewActivity: handleCreateNewActivityClick,
  };

  const GetFilteredAfterSaleClients = async () => {
    try {
      setIsLoading(true);

      const body = {
        ...filters,
        ContactId: filters.ContactId?.trim(),
        assignedAgent: filters.assignedAgent?.userId,
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      };

      const res = await FilterAfterSalesClient(body);

      if (!(res && res.status && res.status !== 200)) {
        setClientData({
          result: res?.result || [],
          totalCount: res?.totalCount || 0,
        });
      }
    } catch (error) {
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: translate("Something-went-wrong"),
        onClose: () => setAlertBoxContent(null),
      });
      console.error("error fetching AfterSalesClientData:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!stickyPagination.current) return;
          if (entry.isIntersecting) {
            stickyPagination.current.style.display = "none";
          } else {
            stickyPagination.current.style.display = "block";
          }
        });
      },
      {
        root: null,
        threshold: 1,
      }
    );

    if (dropPagination.current) {
      observer.observe(dropPagination.current);
    }

    return () => {
      if (dropPagination.current) {
        observer.unobserve(dropPagination.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isNewLayout) {
      setStickyBottomBox(
        <CustomPagination
          hideInMobile
          ref={stickyPagination}
          currentPage={pagination.pageIndex}
          inputValue={inputValue}
          setInputValue={setInputValue}
          totalItems={clientData.totalCount}
          itemsPerPage={pagination.pageSize}
          onPageChange={(page) =>
            setPagination((prev) => ({ ...prev, pageIndex: page }))
          }
          onItemsPerPageChange={(items) =>
            setPagination((prev) => ({ ...prev, pageSize: items }))
          }
          isLoading={isLoading}
          isSticky
        />
      );
    }

    return () => {
      if (isNewLayout) {
        setStickyBottomBox(null);
      }
    };
  }, [isLoading, isNewLayout]);

  useEffect(() => {
    if (!isNewLayout) {
      dispatch(TOGGLE_LAYOUT());
    }
    GetFilteredAfterSaleClients();
  }, [filters, pagination]);

  return isNewLayout ? (
    <Box className={styles.container}>
      <CustomBreadCrumb
        breadcrumbs={[
          {
            label: translate("bread-crump-parent"),
            nonClickable: true,
          },
        ]}
      >
        <CustomBadge
          Style={{
            padding: "4px 8px",
            borderRadius: "6px",
            lineHeight: "20px",
          }}
          label={translate("bread-crump-current")}
          BackgroundColor={palette.breadcrumbs.brand_bg_hover}
          BorderColor={palette.breadcrumbs.brand_bg_hover}
          Color={palette.breadcrumbs.brand_fg_hover}
        />
      </CustomBreadCrumb>
      <PageTitle
        wrapperClasses={styles.title}
        title={translate("page-title")}
        subTitle={translate("page-subtitle")}
        hideDivider
      >
        <CustomPermission
          permissionsList={Object.values(AfterSalePermissions)}
          permissionsId={
            AfterSalePermissions.AssignAfterSalesOwner.permissionsId
          }
        >
          <CustomButton
            boxShadow="xs"
            size="lg"
            variant="outlined"
            color="primary"
            onClick={() => {
              setIsDialogOpen((prev) => ({ ...prev, AssignOwnerDialog: true }));
            }}
            style={{ width: "169px" }}
            disabled={checkedCards.length === 0}
            startIcon={
              <UserCheck01
                width="20"
                height="20"
                fill={palette.button.primary_fg}
              />
            }
          >
            {translate("assign-owner")}
          </CustomButton>
        </CustomPermission>
      </PageTitle>
      <Box style={{ marginTop: "12px" }}>
        {isLoading ? (
          <AfterSalesSummaryCardsListSkeleton numberOfCards={3} />
        ) : (
          <AfterSalesSummaryCards cardsData={clientData.result[0]} />
        )}
      </Box>
      <Box style={{ marginTop: "12px" }}>
        <FiltersSection filters={filters} setFilters={setFilters} />
      </Box>
      <Box className={styles.gridContainer}>
        {isLoading ? (
          Array.from({ length: 25 }).map((_, index) => (
            <AfterSalesClientCardSkeleton key={index} index={index} />
          ))
        ) : clientData?.result?.length > 0 ? (
          clientData?.result?.map(
            (item) =>
              (item?.contactId || item?.id) && (
                <AfterSalesClientCard
                  key={item?.contactId || item?.id}
                  isChecked={checkedCards.includes(item?.contactId || item?.id)}
                  handleCardCheck={handleOnCardCheck}
                  cardActions={cardActions}
                  item={item}
                  hasCheckedCards={checkedCards.length > 0}
                  refetch={GetFilteredAfterSaleClients}
                />
              )
          )
        ) : (
          <NoDataFound title={translate("after-sales-clients")} />
        )}
      </Box>
      <div ref={dropPagination}>
        <CustomPagination
          totalItems={clientData?.totalCount}
          itemsPerPage={pagination.pageSize}
          currentPage={pagination.pageIndex}
          inputValue={inputValue}
          setInputValue={setInputValue}
          onPageChange={(page) =>
            setPagination((prev) => ({ ...prev, pageIndex: page }))
          }
          onItemsPerPageChange={(items) =>
            setPagination((prev) => ({ ...prev, pageSize: items }))
          }
          isLoading={isLoading}
        />
      </div>
      <LinkOfferDialog
        open={isDialogOpen.LinkOfferDialog}
        contactDetails={selectedClient}
        onClose={handleDialogClose}
      />

      {isDialogOpen.ActivitiesDialog && (
        <AddNewActivityModalDialog
          isEdit={false}
          isGeneralDialog
          activeItem={null}
          setDialogSelect={() => {}}
          DialogSelect={5}
          setisOpenDialog={setIsDialogOpen}
          onOpen={isDialogOpen.ActivitiesDialog}
          onClose={() => {
            setIsDialogOpen((item) => ({
              ...item,
              ActivitiesDialog: false,
            }));
          }}
          isAfterSales
          contactId={selectedClient?.contactId ?? ""}
        />
      )}
      <CreateLeadDialog
        contactId={selectedClient?.contactId ?? ""}
        open={isDialogOpen.CreateNewLeadDialog}
        setOpen={handleCreateNewLeadCloseClick}
      />
      <AssignOwnerDialog
        open={isDialogOpen.AssignOwnerDialog}
        onClose={handleAssignOwnerCloseClick}
        contactIds={checkedCards}
        refetch={GetFilteredAfterSaleClients}
      />
    </Box>
  ) : null;
}
